import React from 'react';
import { Target, Eye, Heart, CheckCircle } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Heart,
      title: 'Client-Centric Approach',
      description: 'We prioritize our clients\' needs and provide personalized solutions for every business.',
    },
    {
      icon: CheckCircle,
      title: 'Integrity & Transparency',
      description: 'We maintain the highest standards of professional ethics and transparent communication.',
    },
    {
      icon: Target,
      title: 'Excellence in Service',
      description: 'We strive for excellence in every service we deliver, ensuring client satisfaction.',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="about-page">
      <section
        className="relative h-[400px] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(to right, rgba(15, 23, 42, 0.9), rgba(15, 23, 42, 0.7)), url('https://images.pexels.com/photos/7581114/pexels-photo-7581114.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`,
        }}
        data-testid="about-hero-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            About <span className="bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text text-transparent">NextGen Filling</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto" style={{ fontFamily: 'Inter, sans-serif' }}>
            Your trusted partner for business success across India
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Who We Are
              </h2>
              <p className="text-base text-slate-600 mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
                NextGen Filling Association is a leading CA and business registration firm dedicated to helping entrepreneurs and businesses navigate the complex landscape of company formation, taxation, and compliance in India.
              </p>
              <p className="text-base text-slate-600 mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
                With years of experience and a team of expert chartered accountants, we have successfully assisted thousands of businesses in establishing their legal presence and maintaining regulatory compliance.
              </p>
              <p className="text-base text-slate-600" style={{ fontFamily: 'Inter, sans-serif' }}>
                Our commitment to excellence, transparency, and client satisfaction has made us a trusted name in the industry, serving businesses across various sectors and sizes.
              </p>
            </div>
            <div>
              <img
                src="https://images.pexels.com/photos/7581114/pexels-photo-7581114.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Team collaboration"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-lg mb-4">
                <Target className="text-blue-800" size={32} />
              </div>
              <h3 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Our Mission
              </h3>
              <p className="text-base text-slate-600" style={{ fontFamily: 'Inter, sans-serif' }}>
                To empower entrepreneurs and businesses by providing expert guidance, efficient services, and comprehensive support for all their registration, compliance, and taxation needs, making business setup seamless and stress-free.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="flex items-center justify-center w-16 h-16 bg-amber-100 rounded-lg mb-4">
                <Eye className="text-amber-600" size={32} />
              </div>
              <h3 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Our Vision
              </h3>
              <p className="text-base text-slate-600" style={{ fontFamily: 'Inter, sans-serif' }}>
                To become India's most trusted and preferred partner for business services, recognized for our commitment to excellence, innovation, and client success. We aim to simplify the business ecosystem and contribute to India's entrepreneurial growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Our Core Values
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="bg-slate-50 p-8 rounded-xl border border-slate-200 hover:border-amber-400/50 transition-colors text-center"
                  data-testid={`value-card-${index}`}
                >
                  <div className="flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mx-auto mb-4">
                    <Icon className="text-amber-600" size={28} />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {value.title}
                  </h3>
                  <p className="text-slate-600" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-semibold mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Why Choose NextGen Filling
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-400 mb-2">10+</div>
              <p className="text-slate-300">Years of Experience</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-400 mb-2">5000+</div>
              <p className="text-slate-300">Happy Clients</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-400 mb-2">25+</div>
              <p className="text-slate-300">Services Offered</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-400 mb-2">100%</div>
              <p className="text-slate-300">Client Satisfaction</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;